import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Container, Paper, Typography, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../common/Loading.component';
import { useParams } from 'react-router-dom';
import ListView from '../../../views/List.view';
import ToolbarComponent from '../../toolbar/Toolbar.component';
import _ from 'lodash';
import { TAB_ORDERED } from '../../../module/listItem/conf/Data.conf';

const TabOverview = ({
  module: { tag, query, filter, list, search, action },
  mainTag,
  hasDetail = true,
  viewTag = true,
  onChangeTab = () => {},
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { jwt } = useSelector((state) => state.login);
  const {
    data: { loading, data },
    list: {
      selected,
      pagination: { page },
    },
  } = useSelector((state) => state[tag]);
  const _query = useSelector(query.all.selector);
  const firstRenderRef = useRef(true);
  const userAuth = useSelector((state) => state.login?.user);
  const hasPermissions = useMemo(
    () => userAuth.permissions[_.toUpper(mainTag || tag)]?.READ === 'true',
    [userAuth, mainTag, tag]
  );

  const onClick = useCallback(
    (item) => {
      onChangeTab({ tab: TAB_ORDERED, item: item });
    },
    [onChangeTab]
  );

  const fetchData = useCallback(() => {
    const params = id ? { id } : {};
    dispatch(
      query.all.action({
        settings: query.all.settings,
        token: jwt,
        params,
        query: { ..._query, populate: query.all.populate },
      })
    );
  }, [_query, dispatch, jwt, query, id]);

  useEffect(() => {
    if (_.isEmpty(selected) && hasPermissions) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [_query, dispatch, jwt, query, id, hasPermissions, selected]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (hasPermissions) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [page, firstRenderRef]);

  const onSelectItem = useCallback(
    (row) => {
      dispatch(list.actions.selected({ id: row.id, page }));
    },
    [dispatch, list, page]
  );

  const onSelectAll = useCallback(
    (isChecked) => {
      if (isChecked) {
        dispatch(list.actions.selectedAll({ [page]: [] }));
      } else {
        const all = _.map(data, (item) => item?.id);
        dispatch(list.actions.selectedAll({ [page]: all }));
      }
    },
    [dispatch, list, data, page]
  );

  useEffect(() => {
    if (list.actions.resetSelect) dispatch(list.actions.resetSelect());
  }, [list, dispatch]);

  return (
    <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
      {viewTag && (
        <Box display={'flex'} justifyContent='space-between' padding={3}>
          <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
            {_.upperFirst(tag)}
          </Typography>
        </Box>
      )}
      <Paper variant='outlined'>
        <ToolbarComponent
          tag={tag}
          search={search}
          filter={filter}
          add={list?.add}
          link={list?.link}
          list={list}
          action={action}
          query={query}
        />
        {loading && <Loading />}
        {hasPermissions && (
          <ListView
            list={list}
            onClick={hasDetail ? onClick : () => {}}
            onSelectItem={onSelectItem}
            selected={selected}
            onSelectAll={onSelectAll}
          />
        )}
      </Paper>
    </Container>
  );
};

export default TabOverview;
